/**
 * Historical Analysis Dialog
 * 
 * Shows pattern recognition results after historical data upload
 */

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { CheckCircle, TrendingUp, Zap } from 'lucide-react';

interface Pattern {
  id: string;
  key: string;
  debitAccount: string;
  creditAccount: string;
  vatRate: number;
  frequency: number;
  consistency: number;
  confidence: number;
}

interface GeneratedRule {
  patternKey: string;
  suggestedDebitAccount: string;
  suggestedCreditAccount: string;
  vatCode: string;
  confidence: number;
  frequency: number;
  reasoning: string;
}

interface AnalysisResult {
  totalTransactions: number;
  analyzedTransactions: number;
  patternsFound: number;
  rulesGenerated: number;
  patterns: Pattern[];
  suggestedRules: GeneratedRule[];
  avgConfidence: number;
  processingTimeMs: number;
}

interface HistoricalAnalysisDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  analysisResult: AnalysisResult | null;
  isAnalyzing: boolean;
  onApproveRules: () => void;
}

export const HistoricalAnalysisDialog: React.FC<HistoricalAnalysisDialogProps> = ({
  open,
  onOpenChange,
  analysisResult,
  isAnalyzing,
  onApproveRules,
}) => {
  if (isAnalyzing) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-5 h-5 border-2 border-brand-primary border-t-transparent rounded-full animate-spin" />
              Analyzing Historical Data...
            </DialogTitle>
            <DialogDescription>
              Identifying patterns and generating classification rules
            </DialogDescription>
          </DialogHeader>
          <div className="py-8">
            <Progress value={undefined} className="w-full" />
            <p className="text-sm text-center mt-4 text-brand-text-secondary">
              This may take a few moments depending on the data size
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!analysisResult) return null;

  const { totalTransactions, analyzedTransactions, patternsFound, rulesGenerated, suggestedRules, avgConfidence } = analysisResult;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <CheckCircle className="h-6 w-6 text-green-600" />
            Historical Data Analysis Complete
          </DialogTitle>
          <DialogDescription>
            We've identified {patternsFound} patterns and generated {rulesGenerated} classification rules
          </DialogDescription>
        </DialogHeader>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-blue-700">Transactions Analyzed</span>
              <TrendingUp className="h-4 w-4 text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-blue-900">{analyzedTransactions}</p>
            <p className="text-xs text-blue-600 mt-1">of {totalTransactions} total</p>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-green-700">Patterns Found</span>
              <Zap className="h-4 w-4 text-green-600" />
            </div>
            <p className="text-2xl font-bold text-green-900">{patternsFound}</p>
            <p className="text-xs text-green-600 mt-1">{rulesGenerated} rules generated</p>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-purple-700">Avg Confidence</span>
              <CheckCircle className="h-4 w-4 text-purple-600" />
            </div>
            <p className="text-2xl font-bold text-purple-900">{avgConfidence.toFixed(0)}%</p>
            <p className="text-xs text-purple-600 mt-1">AI accuracy prediction</p>
          </div>
        </div>

        {/* Top Patterns */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-brand-primary">
              Top Learned Patterns
            </h3>
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
              {rulesGenerated} rules ready
            </Badge>
          </div>

          {suggestedRules.length === 0 ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                <i className="fa-solid fa-info-circle mr-2"></i>
                No high-confidence patterns found. Upload more historical data or train the AI manually with new transactions.
              </p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {suggestedRules.slice(0, 10).map((rule, index) => (
                <div
                  key={index}
                  className="bg-white border border-brand-border rounded-lg p-4 hover:border-brand-primary transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-medium text-brand-primary">{rule.patternKey}</span>
                        <Badge
                          variant="outline"
                          className={
                            rule.confidence >= 95
                              ? 'bg-green-50 text-green-700 border-green-200'
                              : rule.confidence >= 90
                              ? 'bg-blue-50 text-blue-700 border-blue-200'
                              : 'bg-orange-50 text-orange-700 border-orange-200'
                          }
                        >
                          {rule.confidence.toFixed(0)}% confidence
                        </Badge>
                      </div>
                      <div className="text-sm space-y-1">
                        <div className="flex items-center gap-2 text-brand-text-secondary">
                          <span className="font-medium">→</span>
                          <span>Debit: {rule.suggestedDebitAccount}</span>
                          <span>•</span>
                          <span>Credit: {rule.suggestedCreditAccount}</span>
                          <span>•</span>
                          <span>VAT: {rule.vatCode}%</span>
                        </div>
                        <p className="text-xs text-brand-text-secondary">
                          <i className="fa-solid fa-chart-line mr-1"></i>
                          {rule.reasoning}
                        </p>
                      </div>
                    </div>
                    <div className="ml-4">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    </div>
                  </div>
                </div>
              ))}

              {suggestedRules.length > 10 && (
                <p className="text-sm text-center text-brand-text-secondary">
                  + {suggestedRules.length - 10} more rules...
                </p>
              )}
            </div>
          )}
        </div>

        {/* Expected Impact */}
        <div className="bg-brand-secondary border border-brand-primary rounded-lg p-4 mt-6">
          <h4 className="font-semibold text-brand-primary mb-2">Expected Impact</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div className="flex items-start">
              <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <span>
                <strong>AI Accuracy:</strong> {avgConfidence.toFixed(0)}% (vs 75% without historical data)
              </span>
            </div>
            <div className="flex items-start">
              <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <span>
                <strong>Auto-Classification:</strong> {rulesGenerated} transaction types will be automatic
              </span>
            </div>
            <div className="flex items-start">
              <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <span>
                <strong>Time Saved:</strong> ~{Math.round(rulesGenerated * 0.5)} minutes per day
              </span>
            </div>
            <div className="flex items-start">
              <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <span>
                <strong>Manual Reviews:</strong> Reduced by ~{Math.round((avgConfidence - 75) / 25 * 100)}%
              </span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Review Later
          </Button>
          <Button
            onClick={() => {
              onApproveRules();
              onOpenChange(false);
            }}
            className="bg-brand-primary hover:bg-brand-primary/90"
            disabled={rulesGenerated === 0}
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Approve {rulesGenerated} Rules
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
